/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {defineMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  AuthHelpers, useAuthHelper, useCustomer, useCustomerBaskets, useCustomerId, useCustomerType, useShopperBasketsMutation,
} from '@salesforce/commerce-sdk-react';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index';
import LoginForm from '@salesforce/retail-react-app/app/components/login';
import RegisterForm from '@salesforce/retail-react-app/app/components/register';
import ResetPasswordForm from '@salesforce/retail-react-app/app/components/reset-password';
import {
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, useDisclosure, useToast,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  API_ERROR_MESSAGE, CREATE_ACCOUNT_FIRST_ERROR_MESSAGE, FEATURE_UNAVAILABLE_ERROR_MESSAGE, LOGIN_TYPES,
  PASSWORDLESS_ERROR_MESSAGES, USER_NOT_FOUND_ERROR,
} from '@salesforce/retail-react-app/app/constants';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {usePasswordReset} from '@salesforce/retail-react-app/app/hooks/use-password-reset';
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous';
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils';
import {isServer, noop} from '@salesforce/retail-react-app/app/utils/utils';

export const LOGIN_VIEW = 'login';
export const REGISTER_VIEW = 'register';
export const PASSWORD_VIEW = 'password';
export const EMAIL_VIEW = 'email';

const LOGIN_ERROR = defineMessage({
  defaultMessage: "Something's not right with your email or password. Try again.",
  id: 'auth_modal.error.incorrect_email_or_password',
});

export const AuthModal = ({
  initialView = LOGIN_VIEW,
  initialEmail = '',
  onLoginSuccess = noop,
  onRegistrationSuccess = noop,
  isOpen,
  onOpen,
  onClose,
  isPasswordlessEnabled = false,
  isSocialEnabled = false,
  idps = [],
  ...props
}) => {
  const {formatMessage} = useIntl();
  const customerId = useCustomerId();
  const {isRegistered, customerType} = useCustomerType();
  const prevAuthType = usePrevious(customerType);

  const customer = useCustomer(
    {parameters: {customerId}},
    {enabled: !!customerId && isRegistered}
  );

  const navigate = useNavigation();
  const [currentView, setCurrentView] = useState(initialView);
  const form = useForm();
  const toast = useToast();
  const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C);
  const register = useAuthHelper(AuthHelpers.Register);
  const appOrigin = useAppOrigin();

  const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD);
  const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState(initialEmail);
  const {getPasswordResetToken} = usePasswordReset();
  const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless);
  const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI;
  const callbackURL = isAbsoluteURL(passwordlessConfigCallback) ?
    passwordlessConfigCallback :
    `${appOrigin}${passwordlessConfigCallback}`;

  const {data: baskets} = useCustomerBaskets(
    {parameters: {customerId}},
    {enabled: !!customerId && !isServer, keepPreviousData: true}
  );
  const mergeBasket = useShopperBasketsMutation('mergeBasket');

  const submitForm = async (data) => {
    form.clearErrors();

    const onLoginSuccess = () => {
      navigate('/account');
    };

    const handlePasswordlessLogin = async (email) => {
      try {
        const redirectPath = window.location.pathname + window.location.search;

        await authorizePasswordlessLogin.mutateAsync({
          userid: email,
          callbackURI: `${callbackURL}?redirectUrl=${redirectPath}`,
        });
        setCurrentView(EMAIL_VIEW);
      } catch (error) {
        const message = USER_NOT_FOUND_ERROR.test(error.message) ?
          formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE) :
          PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message)) ?
            formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE) :
            formatMessage(API_ERROR_MESSAGE);

        form.setError('global', {type: 'manual', message});
      }
    };

    return {
      login: async (data) => {
        if (loginType === LOGIN_TYPES.PASSWORD) {
          try {
            await login.mutateAsync({
              username: data.email,
              password: data.password,
            });
            const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0;
            // we only want to merge basket when the user is logged in as a recurring user
            // only recurring users trigger the login mutation, new user triggers register mutation
            // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
            // if you change logic here, also change it in login page
            const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest';

            if (shouldMergeBasket) {
              mergeBasket.mutate({
                headers: {
                  // This is not required since the request has no body
                  // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                  'Content-Type': 'application/json',
                },
                parameters: {
                  createDestinationBasket: true,
                },
              });
            }
          } catch (error) {
            const message = /Unauthorized/i.test(error.message) ?
              formatMessage(LOGIN_ERROR) :
              formatMessage(API_ERROR_MESSAGE);

            form.setError('global', {type: 'manual', message});
          }
        } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
          setPasswordlessLoginEmail(data.email);
          await handlePasswordlessLogin(data.email);
        }
      },
      register: async (data) => {
        try {
          const body = {
            customer: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              login: data.email,
            },
            password: data.password,
          };

          await register.mutateAsync(body);
          onLoginSuccess();
        } catch (error) {
          form.setError('global', {
            type: 'manual',
            message: formatMessage(API_ERROR_MESSAGE),
          });
        }
      },
      password: async (data) => {
        try {
          await getPasswordResetToken(data.email);
        } catch (e) {
          const message =
            e.response?.status === 400 ?
              formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE) :
              formatMessage(API_ERROR_MESSAGE);

          form.setError('global', {type: 'manual', message});
        }
      },
      email: async () => {
        await handlePasswordlessLogin(passwordlessLoginEmail);
      },
    }[currentView](data);
  };

  // Reset form and local state when opening the modal
  useEffect(() => {
    if (isOpen) {
      setLoginType(LOGIN_TYPES.PASSWORD);
      setCurrentView(initialView);
      form.reset();
    }
  }, [isOpen]);

  // Auto-focus the first field in each form view
  useEffect(() => {
    const initialField = {
      [LOGIN_VIEW]: 'email',
      [REGISTER_VIEW]: 'firstName',
      [PASSWORD_VIEW]: 'email',
    }[currentView];
    const fieldsRef = form.control?.fieldsRef?.current;

    fieldsRef?.[initialField]?.ref.focus();
  }, [form.control?.fieldsRef?.current]);

  // Clear form state when changing views
  useEffect(() => {
    form.reset();
  }, [currentView]);

  useEffect(() => {
    setPasswordlessLoginEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    // Lets determine if the user has either logged in, or registed.
    const loggingIn = currentView === LOGIN_VIEW;
    const registering = currentView === REGISTER_VIEW;
    const isNowRegistered = isOpen && isRegistered && (loggingIn || registering);

    // If the customer changed, but it's not because they logged in or registered. Do nothing.
    if (!isNowRegistered) {
      return;
    }

    // We are done with the modal.
    onClose();

    // Show a toast only for those registed users returning to the site.
    if (loggingIn) {
      toast({
        variant: 'subtle',
        title: `${formatMessage(
          {
            defaultMessage: 'Welcome {name},',
            id: 'auth_modal.info.welcome_user',
          },
          {
            name: customer.data?.firstName || '',
          }
        )}`,
        description: `${formatMessage({
          defaultMessage: "You're now signed in.",
          id: 'auth_modal.description.now_signed_in',
        })}`,
        status: 'success',
        position: 'top-right',
        isClosable: true,
      });

      // Execute action to be performed on successful login
      onLoginSuccess();
    }

    if (registering) {
      // Execute action to be performed on successful registration
      onRegistrationSuccess();
    }
  }, [isRegistered]);

  const onBackToSignInClick = () => (initialView === PASSWORD_VIEW ? onClose() : setCurrentView(LOGIN_VIEW));

  return (
    <Modal
      closeOnOverlayClick={false}
      data-testid="sf-auth-modal"
      isOpen={isOpen}
      size="sm"
      onClose={onClose}
      onOpen={onOpen}
      {...props}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton
          aria-label={formatMessage({
            id: 'auth_modal.button.close.assistive_msg',
            defaultMessage: 'Close login form',
          })}
        />
        <ModalBody bg="white" marginTop={14} paddingBottom={14} pb={8}>
          {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
            <LoginForm
              clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
              form={form}
              handleForgotPasswordClick={() => setCurrentView(PASSWORD_VIEW)}
              handlePasswordlessLoginClick={() => setLoginType(LOGIN_TYPES.PASSWORDLESS)}
              idps={idps}
              isPasswordlessEnabled={isPasswordlessEnabled}
              isSocialEnabled={isSocialEnabled}
              setLoginType={setLoginType}
              submitForm={submitForm}
            />
          )}
          {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
            <RegisterForm
              clickSignIn={onBackToSignInClick}
              form={form}
              submitForm={submitForm}
            />
          )}
          {currentView === PASSWORD_VIEW && (
            <ResetPasswordForm
              clickSignIn={onBackToSignInClick}
              form={form}
              submitForm={submitForm}
            />
          )}
          {currentView === EMAIL_VIEW && (
            <PasswordlessEmailConfirmation
              email={passwordlessLoginEmail}
              form={form}
              submitForm={submitForm}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

AuthModal.propTypes = {
  initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, EMAIL_VIEW]),
  initialEmail: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoginSuccess: PropTypes.func,
  onRegistrationSuccess: PropTypes.func,
  isPasswordlessEnabled: PropTypes.bool,
  isSocialEnabled: PropTypes.bool,
  idps: PropTypes.arrayOf(PropTypes.string),
};

/**
 *
 * @param {('register'|'login'|'password'|'email')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {passwordless = {}, social = {}} = getConfig().app.login || {};

  return {
    initialView,
    isOpen,
    onOpen,
    onClose,
    isPasswordlessEnabled: !!passwordless?.enabled,
    isSocialEnabled: !!social?.enabled,
    idps: social?.idps,
  };
};
